/**
 * Error Handling and Validation Utilities
 * Based on the Comprehensive Subscription System Guide
 */

import { isValidEmail } from "./validation";

// API Error Types
export interface APIError {
  message: string;
  statusCode: number;
  details?: any;
  type:
    | "validation"
    | "authentication"
    | "authorization"
    | "network"
    | "server"
    | "unknown";
}

export interface ValidationError {
  field: string;
  message: string;
}

// Error Classification Function
export const handleAPIError = (error: any): APIError => {
  const response = error.response;
  const statusCode = response?.status || 0;
  const data = response?.data;

  let type: APIError["type"] = "unknown";
  let message = "An unexpected error occurred";

  switch (statusCode) {
    case 400:
      type = "validation";
      message = data?.message || "Invalid request data";
      break;
    case 401:
      type = "authentication";
      message = "Please log in to continue";
      break;
    case 403:
      type = "authorization";
      message = "You do not have permission to perform this action";
      break;
    case 404:
      message = "Resource not found";
      break;
    case 422:
      type = "validation";
      message = data?.message || "Validation failed";
      break;
    case 429:
      message = "Too many requests. Please try again later.";
      break;
    case 500:
    case 502:
    case 503:
      type = "server";
      message = "Server error. Please try again later.";
      break;
    case 0:
      type = "network";
      message = "Network error. Please check your connection.";
      break;
    default:
      if (data?.message) {
        message = data.message;
      }
  }

  return {
    message,
    statusCode,
    details: data,
    type,
  };
};

// Validation Functions
export const validateSubscriptionPurchase = (
  selectedPlan: any,
  paymentMethod: string | null,
  user: any,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!selectedPlan) {
    errors.push({
      field: "plan",
      message: "Please select a subscription plan",
    });
  }

  if (!paymentMethod) {
    errors.push({
      field: "paymentMethod",
      message: "Please select a payment method",
    });
  }

  if (!user?.companyId) {
    errors.push({
      field: "user",
      message: "User must be associated with a company",
    });
  }

  if (selectedPlan && !selectedPlan.isActive) {
    errors.push({
      field: "plan",
      message: "Selected plan is no longer available",
    });
  }

  return errors;
};

export const validateFileUpload = (
  file: File,
  allowedTypes: string[] = [".pdf", ".jpg", ".jpeg", ".png"],
  maxSizeMB: number = 10,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // File size validation
  if (file.size > maxSizeMB * 1024 * 1024) {
    errors.push({
      field: "file",
      message: `File size must be less than ${maxSizeMB}MB`,
    });
  }

  // File type validation
  const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
  if (!allowedTypes.includes(fileExtension)) {
    errors.push({
      field: "file",
      message: `File type not supported. Accepted types: ${allowedTypes.join(", ")}`,
    });
  }

  // File name validation
  if (file.name.length > 255) {
    errors.push({
      field: "file",
      message: "File name is too long (max 255 characters)",
    });
  }

  // Empty file check
  if (file.size === 0) {
    errors.push({
      field: "file",
      message: "File appears to be empty",
    });
  }

  return errors;
};

export const validateEmail = (email: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!email) {
    errors.push({ field: "email", message: "Email is required" });
  } else if (!isValidEmail(email)) {
    errors.push({
      field: "email",
      message: "Please enter a valid email address",
    });
  }

  return errors;
};

export const validatePassword = (password: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!password) {
    errors.push({ field: "password", message: "Password is required" });
    return errors;
  }

  if (password.length < 8) {
    errors.push({
      field: "password",
      message: "Password must be at least 8 characters long",
    });
  }

  if (!/(?=.*[a-z])/.test(password)) {
    errors.push({
      field: "password",
      message: "Password must contain at least one lowercase letter",
    });
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push({
      field: "password",
      message: "Password must contain at least one uppercase letter",
    });
  }

  if (!/(?=.*\d)/.test(password)) {
    errors.push({
      field: "password",
      message: "Password must contain at least one number",
    });
  }

  if (!/(?=.*[!@#$%^&*])/.test(password)) {
    errors.push({
      field: "password",
      message: "Password must contain at least one special character",
    });
  }

  return errors;
};

// Network Retry Logic
export const retryAPICall = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000,
  backoffMultiplier: number = 2,
): Promise<T> => {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Don't retry for certain error types
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay with exponential backoff
      const delay = delayMs * Math.pow(backoffMultiplier, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

// Utility Functions
export const formatErrorMessage = (error: any): string => {
  if (typeof error === "string") return error;
  if (error?.message) return error.message;
  if (error?.response?.data?.message) return error.response.data.message;
  return "An unexpected error occurred";
};

export const isNetworkError = (error: any): boolean => {
  return (
    !error.response &&
    (error.code === "NETWORK_ERROR" || error.message === "Network Error")
  );
};

export const isValidationError = (error: any): boolean => {
  return error.response?.status === 400 || error.response?.status === 422;
};

export const extractValidationErrors = (error: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  const data = error.response?.data;

  if (data?.errors) {
    Object.entries(data.errors).forEach(([field, messages]) => {
      if (Array.isArray(messages)) {
        messages.forEach((message: string) => {
          errors.push({ field, message });
        });
      } else if (typeof messages === "string") {
        errors.push({ field, message: messages });
      }
    });
  }

  return errors;
};

/**
 * Extracts and formats error messages from API error responses.
 * Specifically handles role-related error message formatting.
 *
 * @param error - The error object from the API call
 * @param defaultMessage - Default error message if extraction fails
 * @returns Formatted error message string
 */
export const extractRoleErrorMessage = (
  error: any,
  defaultMessage: string = "An error occurred",
): string => {
  // Extract error message from API error response
  // Backend sends: { success: false, error: { code, message } }
  let errorMessage =
    error?.message ||
    error?.response?.data?.error?.message ||
    error?.error?.message ||
    defaultMessage;

  // Format "Role name already exists: <name>" to "Role name "<name>" already exists"
  const roleNameMatch = errorMessage.match(
    /^Role name already exists:\s*(.+)$/i,
  );
  if (roleNameMatch) {
    const roleName = roleNameMatch[1].trim();
    errorMessage = `Role name "${roleName}" already exists`;
  }

  return errorMessage;
};

/**
 * Extracts an error message from an unknown error object.
 * Handles API error responses with `response.data.message` and standard Error instances.
 *
 * @param error - The caught error (typed as unknown)
 * @param defaultMessage - Fallback message when extraction fails
 * @returns The extracted or default error message
 */
export const getErrorMessage = (
  error: unknown,
  defaultMessage: string,
): string => {
  if (error && typeof error === "object" && "response" in error) {
    const resp = error as { response?: { data?: { message?: string } } };
    return resp.response?.data?.message || defaultMessage;
  }
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }
  // Handle plain ApiError objects thrown by apiClient (not Error instances)
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === "string" && msg) return msg;
  }
  return defaultMessage;
};
